/**
 * The SPA session as a writer (PLAN §6.1): its HLC clock, origin id, `ord`
 * writer tag, Yjs client id, row ids and clientOpIds — the CLI's
 * `apps/cli/src/writer.ts`, for a browser tab.
 *
 * **One writer per editor session.** HLCs must stay unique per writer (D6),
 * and a user may have the rundown open in two tabs: a fresh random node id per
 * session keeps two tabs from minting the same HLC in one millisecond.
 * Causality: every row the session receives is `observe`d, so a local edit is
 * always newer than what the user was looking at when they made it — the
 * "latest wins" of PLAN §6.2 means the latest the user saw, not the latest
 * wall clock.
 */
import { createHlcClock, type HlcClock } from '@acp/domain/hlc.js';
import { writerTagFor } from '@acp/domain/ordering.js';
import type { Row } from '@acp/domain/row.js';
import { createUlidFactory } from '@acp/domain/ulid.js';

export type Writer = {
  readonly nodeId: string;
  readonly clock: HlcClock;
  /** The `ord` suffix for this writer (`ordering.ts`). */
  readonly ordTag: string;
  /**
   * The Yjs client id script edits are written under (a uint32). A Yjs client
   * numbers its insertions, and each script edit is built from the text state
   * the editor holds; if an edit is rolled back after the server may have
   * applied it (a request that failed without an answer), the next edit would
   * reuse those numbers for different characters, which Yjs cannot tell apart
   * — so after any unanswered or refused text edit the session `rotateTextClient`s.
   */
  readonly textClientId: () => number;
  readonly rotateTextClient: () => void;
  readonly newId: () => string;
  readonly newOpId: () => string;
  /** Fold every HLC on `row` into the clock. */
  readonly observe: (row: Row) => void;
};

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';

export function createWriter(nowMs: () => number, randomBytes: (count: number) => Uint8Array): Writer {
  const suffix = Array.from(randomBytes(10), (b) => ALPHABET.charAt(b % ALPHABET.length)).join('');
  const nodeId = `web-${suffix}`;
  const clock = createHlcClock(nodeId, nowMs, null);
  const newId = createUlidFactory(nowMs, randomBytes);
  // A Yjs client id is a uint32.
  const newClientId = (): number => new DataView(Uint8Array.from(randomBytes(4)).buffer).getUint32(0);
  let textClientId = newClientId();
  return {
    nodeId,
    clock,
    ordTag: writerTagFor(nodeId),
    textClientId: () => textClientId,
    rotateTextClient: () => {
      textClientId = newClientId();
    },
    newId,
    newOpId: () => `web-${newId()}`,
    observe: (row) => {
      for (const hlc of Object.values(row.h)) clock.receive(hlc);
    },
  };
}
